import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import type { EndUser } from '../lib/types'
import { formatRowInput, isDefaultWord, isRowDirty, rowTarget, toInt } from '../lib/userLimits'
import { Avatar } from './ui/Avatar'
import { UsageBar } from './ui/UsageBar'

// The prototype's `ICON` set in `initUserLimits` (subscription-portal.html).
function ResetIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <g transform="rotate(-45 12 12)">
        <rect x="3" y="7" width="18" height="10" rx="2" />
        <line x1="7" y1="7" x2="7" y2="17" />
      </g>
    </svg>
  )
}
function SaveIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="var(--accent)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}
function DiscardIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="var(--danger)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}
function DeleteIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="var(--danger)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  )
}

const ICON_BUTTON =
  'w-7 h-7 flex items-center justify-center rounded-md hover:bg-[var(--bg-hover)] disabled:opacity-30 disabled:pointer-events-none'
const STEP_BUTTON =
  'w-[30px] h-[30px] text-[16px] bg-[var(--bg-card)] text-[var(--text-primary)] hover:bg-[rgba(128,128,128,.12)]'

/** The field's text while nothing is typed: the user's own figure, the word
 * "Default" for a row on the default, or — with the default drafted off —
 * the figure Save would give them; empty for no limit. */
function savedRowText(
  user: EndUser,
  onDefault: boolean,
  defaultPreview: number | null,
  word: string,
  fmt: (n: number) => string
): string {
  if (user.userLimit !== null) return fmt(user.userLimit)
  if (onDefault) return word
  return defaultPreview === null ? '' : fmt(defaultPreview)
}

/** The bar's limit (`updateRowBar`): what is typed — an empty field or the
 * word runs at the enforced default — or, untouched, the limit in force. */
function rowBarLimit(
  user: EndUser,
  raw: string | undefined,
  word: string,
  enforcedDefault: number | null,
  defaultPreview: number | null
): number | null {
  if (raw !== undefined) return rowTarget(raw, word) ?? enforcedDefault
  return user.userLimit === null ? defaultPreview : user.effectiveLimit
}

/** `barHtml`'s share: a dash with no limit, "Over limit" for usage against 0.
 * The whole percent, written the active language's way ("42%", "42 %",
 * "%42"), ungrouped as the prototype's `Math.round(...) + '%'` is. */
function usedShare(t: TFunction, language: string, used: number, limit: number | null): string {
  if (limit === null) return t('common.dash')
  const percent = percentFormat(language)
  if (limit > 0) return percent.format(Math.round((used / limit) * 100) / 100)
  return used > 0 ? t('userLimits.overLimit') : percent.format(0)
}

// Built once per language, not once per row per render.
const PERCENT_FORMATS = new Map<string, Intl.NumberFormat>()
function percentFormat(language: string): Intl.NumberFormat {
  let format = PERCENT_FORMATS.get(language)
  if (!format) {
    format = new Intl.NumberFormat(language, { style: 'percent', maximumFractionDigits: 0, useGrouping: false })
    PERCENT_FORMATS.set(language, format)
  }
  return format
}

function resetTitleFor(t: TFunction, defaultEnabled: boolean, onDefault: boolean): string {
  if (!defaultEnabled) return t('userLimits.reset.noDefault')
  return onDefault ? t('userLimits.reset.alreadyDefault') : t('userLimits.resetToDefault')
}

/** One user's row (the prototype's `.limit-card`, `renderRows`): the user
 * glyph and email (§16h — the email is the only identity), the usage bar,
 * the editable limit, then Reset, Save, Discard and Delete. Delete is live in
 * both registration modes and only asks the page to confirm (`onDelete`);
 * the page owns the confirmation and the write (MS-996, §53).
 *
 * The typed value (`raw`) is owned by the page, so it can register every
 * dirty row with the unsaved-changes guard (OQ7) and keep it across a
 * re-sort. `onDefault` and `defaultPreview` come from the page's default
 * *draft*, as the prototype's rows read the live default: a row on the
 * default shows the word "Default" and its bar follows the figure being
 * typed into the default, without re-sorting (T-LIM-06).
 *
 * Reset alone reads the *saved* default (`savedDefaultEnabled`): it writes
 * at once, so it may only put a user on a default that is already enforced —
 * never on one only drafted on, which a Discard or a refused Save would leave
 * the user with no limit at all (§16j, never silently widen;
 * ARCHITECTURE_DECISIONS.md §46). */
export function LimitRow({
  user,
  raw,
  onRawChange,
  onDefault,
  defaultEnabled,
  savedDefaultEnabled,
  defaultPreview,
  onSave,
  onDelete,
  error,
}: Readonly<{
  user: EndUser
  /** The typed value, or undefined while the row shows what is saved. */
  raw: string | undefined
  onRawChange: (raw: string | undefined) => void
  /** The user follows an enforced default (no limit of their own). */
  onDefault: boolean
  /** The default limit is switched on (in the page's draft). */
  defaultEnabled: boolean
  /** The default limit is switched on as saved — what Reset puts a user on. */
  savedDefaultEnabled: boolean
  /** The figure a user without their own limit runs at, as drafted. */
  defaultPreview: number | null
  /** Save `limit` (null: back on the default); `reset` picks the toast. */
  onSave: (limit: number | null, reset: boolean) => void
  /** Delete was pressed: the page confirms, then removes the user's limit. */
  onDelete: () => void
  /** Why the last page Save could not write this drafted user, shown under
   * the row until they are deleted or Discarded (MS-996 review, §53). */
  error?: string
}>) {
  const { t, i18n } = useTranslation()
  const word = t('userLimits.defaultWord')
  const fmt = (n: number) => n.toLocaleString(i18n.language)
  const errorId = useId()

  const shown = raw ?? savedRowText(user, onDefault, defaultPreview, word, fmt)
  const showsWord = isDefaultWord(shown, word) && shown !== ''
  const limit = rowBarLimit(user, raw, word, defaultEnabled ? defaultPreview : null, defaultPreview)
  const used = user.usageCount
  const over = limit !== null && used > limit
  const dirty = isRowDirty(user, raw, word)
  // `eraserInert`: nothing to reset to, or already there — judged on the
  // saved default, since Reset saves at once.
  const noOwnLimit = user.userLimit === null
  const resetInert = !savedDefaultEnabled || noOwnLimit
  const resetTitle = resetTitleFor(t, savedDefaultEnabled, noOwnLimit)

  function step(direction: 1 | -1) {
    onRawChange(fmt(Math.max(0, toInt(shown) + direction)))
  }

  return (
    <div
      data-testid="limit-row"
      data-email={user.email}
      data-error={error ? true : undefined}
      className="flex items-center gap-[18px] flex-wrap border border-[var(--border)] rounded-[10px] px-4 py-3 bg-[var(--bg-card)] hover:border-[var(--accent)] transition-colors"
    >
      <div className="flex-[1_1_220px] min-w-[200px] flex items-center gap-2.5">
        <Avatar />
        <div className="min-w-0 text-[13.5px] font-semibold leading-[1.2] text-[var(--text-primary)] truncate">
          {user.email}
        </div>
      </div>
      <div className="flex-[0_0_240px] min-w-[200px]">
        <div className="flex justify-between text-[11.5px] text-[var(--text-primary)]">
          <span>
            {fmt(used)} / {limit === null ? t('common.dash') : fmt(limit)}
          </span>
          <span className={over ? 'text-[var(--danger)]' : 'text-[var(--text-tertiary)]'}>
            {usedShare(t, i18n.language, used, limit)}
          </span>
        </div>
        <UsageBar used={used} total={limit} />
      </div>
      <div className="flex-none flex items-center gap-1.5">
        <div className="inline-flex items-center border border-[var(--border)] rounded-lg overflow-hidden">
          <button type="button" onClick={() => step(-1)} className={STEP_BUTTON}>
            −
          </button>
          <input
            type="text"
            inputMode="numeric"
            value={shown}
            onChange={(e) => onRawChange(formatRowInput(e.target.value, word, i18n.language))}
            placeholder={t('userLimits.noLimit')}
            aria-label={t('userLimits.rowLimitAria', { email: user.email })}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            className={`w-24 h-[30px] text-center text-[13px] border-x border-[var(--border)] bg-[var(--bg-card)] ${
              showsWord ? 'italic text-[var(--text-secondary)]' : 'text-[var(--text-primary)]'
            }`}
          />
          <button type="button" onClick={() => step(1)} className={STEP_BUTTON}>
            +
          </button>
        </div>
        <button
          type="button"
          disabled={resetInert}
          onClick={() => onSave(null, true)}
          title={resetTitle}
          aria-label={resetTitle}
          className={`${ICON_BUTTON} text-[var(--text-tertiary)]`}
        >
          <ResetIcon />
        </button>
        <button
          type="button"
          disabled={!dirty}
          onClick={() => onSave(rowTarget(raw ?? '', word), false)}
          title={t('userLimits.saveLimit')}
          aria-label={t('userLimits.saveLimit')}
          className={ICON_BUTTON}
        >
          <SaveIcon />
        </button>
        <button
          type="button"
          disabled={!dirty}
          onClick={() => onRawChange(undefined)}
          title={t('userLimits.discardChange')}
          aria-label={t('userLimits.discardChange')}
          className={ICON_BUTTON}
        >
          <DiscardIcon />
        </button>
        {/* Available in both modes, always behind a confirmation (the
            prototype's `.lc-delete-btn` handler). */}
        <button
          type="button"
          onClick={onDelete}
          title={t('userLimits.deleteUser')}
          aria-label={t('userLimits.deleteUser')}
          className={`${ICON_BUTTON} hover:bg-[var(--danger-bg)]`}
        >
          <DeleteIcon />
        </button>
      </div>
      {/* The Add-user modal's error line, under the whole row. */}
      {error && (
        <div id={errorId} role="alert" className="basis-full -mt-2 text-[12px] text-[var(--danger)]">
          {error}
        </div>
      )}
    </div>
  )
}
